import React, { useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Title2,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  Card,
  Text,
  Dropdown,
  Option,
  Checkbox,
  Caption1
} from '@fluentui/react-components';
import { ArrowSync24Regular, Calendar24Regular, Filter24Regular } from '@fluentui/react-icons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { historyService } from '../../services/historyService';
import type { HistoryResponse } from '../../types';

const useStyles = makeStyles({
  container: {
    padding: '24px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px',
  },
  title: {
    color: tokens.colorNeutralForeground1,
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  filterSection: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '24px',
    padding: '16px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  filterLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  dropdown: {
    minWidth: '120px',
  },
  chartsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  chartFullWidth: {
    width: '100%',
  },
  chartsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '24px',
    '@media (max-width: 1200px)': {
      gridTemplateColumns: '1fr',
    },
  },
  chartCard: {
    padding: '18px',
    backgroundColor: '#4a4a4a',
    minHeight: '320px',
  },
  chartHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '14px',
  },
  chartTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#ffffff',
  },
  chartSubtitle: {
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.6)',
  },
  chartContainer: {
    height: '260px',
    width: '100%',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px',
    gap: '16px',
  },
  errorContainer: {
    marginBottom: '24px',
  },
  noData: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '260px',
    color: 'rgba(255, 255, 255, 0.5)',
  },
});

// Cores consistentes com o Dashboard
const CHART_COLORS = {
  schools: '#0078d4',      // Azul Microsoft
  students: '#22c55e',     // Verde (mesmo do Dashboard)
  teachers: '#f59e0b',     // Amarelo/Laranja
  classes: '#8b5cf6',      // Roxo
  success: '#22c55e',      // Verde
  failure: '#ef4444',      // Vermelho
  dlq: '#ef4444',          // Vermelho
  scheduled: '#22c55e',    // Verde
  // Cores para "Operações por Tipo" (consistentes com Totais por Dia)
  entityStudents: '#22c55e',      // Verde (igual students em Totais)
  entityTeachers: '#f59e0b',      // Amarelo/Laranja (igual teachers em Totais)
  studentEnrollments: '#8b5cf6',  // Roxo (Enrollment Alunos)
  teacherEnrollments: '#06b6d4',  // Ciano/Azul claro (Enrollment Professores)
};

// Tooltip customizado estilo Dashboard
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        padding: '8px 12px',
        borderRadius: '4px',
        boxShadow: tokens.shadow4,
      }}>
        <p style={{ margin: 0, fontWeight: 600, marginBottom: '4px' }}>{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ margin: '2px 0', color: entry.color, fontSize: '13px' }}>
            {entry.name}: {historyService.formatNumber(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Gerar dias de um período (startDate até endDate)
const generateDaysInPeriod = (startDate: Date, endDate: Date): string[] => {
  const days: string[] = [];
  const current = new Date(startDate);
  const end = new Date(endDate);

  while (current <= end) {
    days.push(historyService.formatDateShort(current));
    current.setDate(current.getDate() + 1);
  }
  return days;
};

// Preencher dados faltantes com zeros para um período
const fillMissingDaysInPeriod = (
  data: any[],
  dateKey: string,
  valueKeys: string[],
  startDate: Date,
  endDate: Date
): any[] => {
  const allDays = generateDaysInPeriod(startDate, endDate);
  const dataMap = new Map(data.map(item => [item[dateKey], item]));

  return allDays.map(day => {
    const existing = dataMap.get(day);
    if (existing) {
      return existing;
    }
    const emptyItem: any = { [dateKey]: day };
    valueKeys.forEach(key => {
      emptyItem[key] = 0;
    });
    return emptyItem;
  });
};

// Calcular intervalo para mostrar ~7 ticks no eixo X
const calculateTickInterval = (dataLength: number): number => {
  if (dataLength <= 7) return 0; // Mostrar todos
  if (dataLength <= 15) return 1; // A cada 2 dias
  if (dataLength <= 31) return Math.floor(dataLength / 6) - 1; // ~6-7 ticks
  return Math.floor(dataLength / 7) - 1; // ~7-8 ticks para períodos maiores
};

// 12 meses fixos para visualização mensal
const MONTHS_FIXED = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// Agregar dados por mês (sempre 12 meses fixos)
const aggregateToMonthlyFixed = (
  data: any[],
  dateKey: string,
  valueKeys: string[],
  selectedMonths: number[],
  selectedYear: number
): any[] => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1; // 1-12
  const currentYear = currentDate.getFullYear();

  // Criar mapa de dados por mês
  const monthlyData = new Map<number, any>();

  data.forEach(item => {
    // Extrair mês da data (formato DD/MM ou DD/MM/YYYY)
    const dateParts = item[dateKey].split('/');
    const month = parseInt(dateParts[1], 10); // Mês é a segunda parte

    if (!monthlyData.has(month)) {
      const initial: any = { month };
      valueKeys.forEach(key => initial[key] = 0);
      monthlyData.set(month, initial);
    }

    const existing = monthlyData.get(month)!;
    valueKeys.forEach(key => {
      existing[key] += item[key] || 0;
    });
  });

  // Determinar último mês relevante (atual ou último selecionado)
  const lastRelevantMonth = selectedMonths.length > 0
    ? Math.min(Math.max(...selectedMonths), selectedYear === currentYear ? currentMonth : 12)
    : (selectedYear === currentYear ? currentMonth : 12);

  // Retornar sempre 12 meses
  return MONTHS_FIXED.map((label, index) => {
    const monthNum = index + 1;
    const isSelected = selectedMonths.length === 0 || selectedMonths.includes(monthNum);
    const monthData = monthlyData.get(monthNum);
    const isFutureMonth = monthNum > lastRelevantMonth;

    const result: any = { date: label };

    if (isFutureMonth) {
      // Mês futuro: null para não desenhar linha
      valueKeys.forEach(key => {
        result[key] = null;
      });
    } else if (isSelected && monthData) {
      // Mês com dados reais
      valueKeys.forEach(key => {
        result[key] = monthData[key];
      });
    } else {
      // Mês não selecionado (no passado): mostra 0
      valueKeys.forEach(key => {
        result[key] = 0;
      });
    }

    return result;
  });
};

export const HistoryPage: React.FC = () => {
  const styles = useStyles();
  const [history, setHistory] = useState<HistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Filtros
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonths, setSelectedMonths] = useState<number[]>([]);
  const [useLast15Days, setUseLast15Days] = useState(true);

  const years = historyService.getAvailableYears();
  const months = historyService.getMonths();

  const loadHistory = useCallback(async () => {
    try {
      setError(null);
      let startDate: Date | undefined;
      let endDate: Date | undefined;

      if (!useLast15Days) {
        const range = historyService.calculateDateRange(selectedYear, selectedMonths);
        startDate = range.startDate;
        endDate = range.endDate;
      }

      const data = await historyService.getAll(startDate, endDate);
      setHistory(data);
    } catch (err: any) {
      console.error('Error loading history:', err);
      setError(err.message || 'Erro ao carregar histórico. Verifique se a API está disponível.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedYear, selectedMonths, useLast15Days]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadHistory();
  };

  const handleYearChange = (_: any, data: any) => {
    setSelectedYear(parseInt(data.optionValue));
    setUseLast15Days(false);
  };

  const handleMonthsChange = (_: any, data: any) => {
    const selected = data.selectedOptions.map((opt: string) => parseInt(opt, 10));
    setSelectedMonths(selected);
    setUseLast15Days(false);
  };

  const handleLast15DaysToggle = () => {
    setUseLast15Days(prev => !prev);
  };

  // Determinar se é visão mensal (0 ou 2+ meses) ou diária (1 mês ou últimos 15 dias)
  const isMonthlyView = !useLast15Days && selectedMonths.length !== 1;

  // Formatar dados para os gráficos
  const rawSummaryData = history?.summary.map(item => ({
    date: historyService.formatDateShort(item.date),
    fullDate: historyService.formatDate(item.date),
    schools: item.totalSchools,
    students: item.totalStudents,
    teachers: item.totalTeachers,
    classes: item.totalClasses,
  })) ?? [];

  const rawOperationsData = history?.operations.map(item => ({
    date: historyService.formatDateShort(item.date),
    fullDate: historyService.formatDate(item.date),
    success: item.successCount,
    failure: item.failureCount,
  })) ?? [];

  // Dados para "Operações por Tipo"
  const rawOperationsByTypeData = history?.operations.map(item => ({
    date: historyService.formatDateShort(item.date),
    fullDate: historyService.formatDate(item.date),
    students: item.byEntityType?.students ?? 0,
    teachers: item.byEntityType?.teachers ?? 0,
    studentEnrollments: item.byEntityType?.studentEnrollments ?? 0,
    teacherEnrollments: item.byEntityType?.teacherEnrollments ?? 0,
  })) ?? [];

  const rawDlqData = history?.dlq.map(item => ({
    date: historyService.formatDateShort(item.date),
    fullDate: historyService.formatDate(item.date),
    count: item.count,
  })) ?? [];

  const rawScheduledData = history?.scheduled.map(item => ({
    date: historyService.formatDateShort(item.date),
    fullDate: historyService.formatDate(item.date),
    count: item.count,
  })) ?? [];

  // Obter período para preenchimento de dias
  const periodStart = history ? new Date(history.startDate) : new Date();
  const periodEnd = history ? new Date(history.endDate) : new Date();

  // Processar dados: visão mensal (12 meses fixos) ou diária
  const summaryChartData = history
    ? isMonthlyView
      ? aggregateToMonthlyFixed(rawSummaryData, 'date', ['schools', 'students', 'teachers', 'classes'], selectedMonths, selectedYear)
      : fillMissingDaysInPeriod(rawSummaryData, 'date', ['schools', 'students', 'teachers', 'classes'], periodStart, periodEnd)
    : [];

  const operationsChartData = history
    ? isMonthlyView
      ? aggregateToMonthlyFixed(rawOperationsData, 'date', ['success', 'failure'], selectedMonths, selectedYear)
      : fillMissingDaysInPeriod(rawOperationsData, 'date', ['success', 'failure'], periodStart, periodEnd)
    : [];

  // Dados para gráfico "Operações por Tipo"
  const operationsByTypeChartData = history
    ? isMonthlyView
      ? aggregateToMonthlyFixed(rawOperationsByTypeData, 'date', ['students', 'teachers', 'studentEnrollments', 'teacherEnrollments'], selectedMonths, selectedYear)
      : fillMissingDaysInPeriod(rawOperationsByTypeData, 'date', ['students', 'teachers', 'studentEnrollments', 'teacherEnrollments'], periodStart, periodEnd)
    : [];

  const dlqChartData = history
    ? isMonthlyView
      ? aggregateToMonthlyFixed(rawDlqData, 'date', ['count'], selectedMonths, selectedYear)
      : fillMissingDaysInPeriod(rawDlqData, 'date', ['count'], periodStart, periodEnd)
    : [];

  const scheduledChartData = history
    ? isMonthlyView
      ? aggregateToMonthlyFixed(rawScheduledData, 'date', ['count'], selectedMonths, selectedYear)
      : fillMissingDaysInPeriod(rawScheduledData, 'date', ['count'], periodStart, periodEnd)
    : [];

  // Calcular intervalo de ticks (0 para visão mensal - mostrar todos os 12)
  const tickInterval = isMonthlyView ? 0 : calculateTickInterval(summaryChartData.length);

  if (loading && !history) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Title2 className={styles.title}>Histórico</Title2>
        </div>
        <div className={styles.loadingContainer}>
          <Spinner size="large" />
          <span>Carregando histórico...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Title2 className={styles.title}>Histórico</Title2>
        <div className={styles.headerActions}>
          <Button
            appearance="primary"
            icon={refreshing ? <Spinner size="tiny" /> : <ArrowSync24Regular />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? 'Atualizando...' : 'Atualizar'}
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className={styles.filterSection}>
        <div className={styles.filterLabel}>
          <Filter24Regular />
          Período:
        </div>

        <Checkbox
          checked={useLast15Days}
          onChange={handleLast15DaysToggle}
          label="Últimos 15 dias"
        />

        <Text style={{ color: tokens.colorNeutralForeground3 }}>ou</Text>

        <Dropdown
          className={styles.dropdown}
          value={selectedYear.toString()}
          selectedOptions={[selectedYear.toString()]}
          onOptionSelect={handleYearChange}
          disabled={useLast15Days}
        >
          {years.map(year => (
            <Option key={year} value={year.toString()} text={year.toString()}>
              {year}
            </Option>
          ))}
        </Dropdown>

        <Dropdown
          className={styles.dropdown}
          multiselect
          placeholder="Todos os meses"
          selectedOptions={selectedMonths.map(m => m.toString())}
          onOptionSelect={handleMonthsChange}
          disabled={useLast15Days}
          style={{ minWidth: '180px' }}
        >
          {months.map(month => (
            <Option key={month.value} value={month.value.toString()} text={month.label.substring(0, 3)}>
              {month.label.substring(0, 3)}
            </Option>
          ))}
        </Dropdown>

        {!useLast15Days && (
          <Text style={{ color: tokens.colorNeutralForeground3, fontSize: '12px' }}>
            {selectedMonths.length === 0 && '(visão mensal)'}
            {selectedMonths.length === 1 && '(visão diária)'}
            {selectedMonths.length > 1 && '(visão mensal)'}
          </Text>
        )}

        {/* Período selecionado - na mesma linha */}
        {history && (
          <Text style={{ color: tokens.colorNeutralForeground3, fontSize: '12px', marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Calendar24Regular />
            {historyService.formatDate(history.startDate)} até {historyService.formatDate(history.endDate)}
          </Text>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className={styles.errorContainer}>
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        </div>
      )}

      {/* Charts Grid */}
      <div className={styles.chartsGrid}>
        {/* Primeira linha: Operações por Tipo (largura total) */}
        <div className={styles.chartFullWidth}>
          <Card className={styles.chartCard}>
            <div className={styles.chartHeader}>
              <span className={styles.chartTitle}>Novas Operações por Tipo</span>
              <span className={styles.chartSubtitle}>Alunos, Professores, Enrollments</span>
            </div>
            <div className={styles.chartContainer}>
              {operationsByTypeChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={operationsByTypeChartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: 'rgba(255, 255, 255, 0.7)' }}
                      tickLine={false}
                      axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                      interval={tickInterval}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: 'rgba(255, 255, 255, 0.7)' }}
                      tickLine={false}
                      axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                      tickFormatter={(value) => value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '11px' }} />
                    <Line type="monotone" dataKey="students" name="Alunos" stroke={CHART_COLORS.entityStudents} strokeWidth={2} dot={{ fill: CHART_COLORS.entityStudents, r: 3 }} activeDot={{ r: 5 }} />
                    <Line type="monotone" dataKey="teachers" name="Professores" stroke={CHART_COLORS.entityTeachers} strokeWidth={2} dot={{ fill: CHART_COLORS.entityTeachers, r: 3 }} activeDot={{ r: 5 }} />
                    <Line type="monotone" dataKey="studentEnrollments" name="Enroll. Alunos" stroke={CHART_COLORS.studentEnrollments} strokeWidth={2} dot={{ fill: CHART_COLORS.studentEnrollments, r: 3 }} activeDot={{ r: 5 }} />
                    <Line type="monotone" dataKey="teacherEnrollments" name="Enroll. Prof." stroke={CHART_COLORS.teacherEnrollments} strokeWidth={2} dot={{ fill: CHART_COLORS.teacherEnrollments, r: 3 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className={styles.noData}>
                  <Caption1>Nenhum dado disponível</Caption1>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Segunda linha: Operações Sucesso/Falha (largura total) */}
        <div className={styles.chartFullWidth}>
          <Card className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <span className={styles.chartTitle}>Novas Operações por {isMonthlyView ? 'Mês' : 'Dia'}</span>
            <span className={styles.chartSubtitle}>Sucesso vs Falha</span>
          </div>
          <div className={styles.chartContainer}>
            {operationsChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={operationsChartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: 'rgba(255, 255, 255, 0.7)' }}
                    tickLine={false}
                    axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                    interval={tickInterval}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'rgba(255, 255, 255, 0.7)' }}
                    tickLine={false}
                    axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                    tickFormatter={(value) => value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '11px' }} />
                  <Line
                    type="monotone"
                    dataKey="success"
                    name="Sucesso"
                    stroke={CHART_COLORS.success}
                    strokeWidth={2}
                    dot={{ fill: CHART_COLORS.success, r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="failure"
                    name="Falha"
                    stroke={CHART_COLORS.failure}
                    strokeWidth={2}
                    dot={{ fill: CHART_COLORS.failure, r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className={styles.noData}>
                <Caption1>Nenhum dado disponível</Caption1>
              </div>
            )}
          </div>
          </Card>
        </div>

        {/* Segunda linha: Totais, DLQ, Reagendamentos (3 colunas) */}
        <div className={styles.chartsRow}>
          {/* Gráfico: Totais */}
          <Card className={styles.chartCard}>
            <div className={styles.chartHeader}>
              <span className={styles.chartTitle}>Totais por {isMonthlyView ? 'Mês' : 'Dia'}</span>
              <span className={styles.chartSubtitle}>Escolas, Alunos, Professores, Classes</span>
            </div>
            <div className={styles.chartContainer}>
              {summaryChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={summaryChartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: 'rgba(255, 255, 255, 0.7)' }}
                      tickLine={false}
                      axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                      interval={tickInterval}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: 'rgba(255, 255, 255, 0.7)' }}
                      tickLine={false}
                      axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                      tickFormatter={(value) => value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '11px' }} />
                    <Line type="monotone" dataKey="schools" name="Escolas" stroke={CHART_COLORS.schools} strokeWidth={2} dot={{ fill: CHART_COLORS.schools, r: 3 }} activeDot={{ r: 5 }} />
                    <Line type="monotone" dataKey="students" name="Alunos" stroke={CHART_COLORS.students} strokeWidth={2} dot={{ fill: CHART_COLORS.students, r: 3 }} activeDot={{ r: 5 }} />
                    <Line type="monotone" dataKey="teachers" name="Professores" stroke={CHART_COLORS.teachers} strokeWidth={2} dot={{ fill: CHART_COLORS.teachers, r: 3 }} activeDot={{ r: 5 }} />
                    <Line type="monotone" dataKey="classes" name="Classes" stroke={CHART_COLORS.classes} strokeWidth={2} dot={{ fill: CHART_COLORS.classes, r: 3 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className={styles.noData}>
                  <Caption1>Nenhum dado disponível</Caption1>
                </div>
              )}
            </div>
          </Card>

          {/* Gráfico: DLQ */}
        <Card className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <span className={styles.chartTitle}>Dead Letter Queue por {isMonthlyView ? 'Mês' : 'Dia'}</span>
            <span className={styles.chartSubtitle}>Mensagens com erro</span>
          </div>
          <div className={styles.chartContainer}>
            {dlqChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dlqChartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: 'rgba(255, 255, 255, 0.7)' }}
                    tickLine={false}
                    axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                    interval={tickInterval}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'rgba(255, 255, 255, 0.7)' }}
                    tickLine={false}
                    axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                    allowDecimals={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="count"
                    name="DLQ"
                    stroke={CHART_COLORS.dlq}
                    strokeWidth={2}
                    dot={{ fill: CHART_COLORS.dlq, r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className={styles.noData}>
                <Caption1>Nenhum dado disponível</Caption1>
              </div>
            )}
          </div>
        </Card>

        {/* Gráfico 4: Mensagens Agendadas */}
        <Card className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <span className={styles.chartTitle}>Reagendamentos por {isMonthlyView ? 'Mês' : 'Dia'}</span>
            <span className={styles.chartSubtitle}>Retries pendentes</span>
          </div>
          <div className={styles.chartContainer}>
            {scheduledChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={scheduledChartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: 'rgba(255, 255, 255, 0.7)' }}
                    tickLine={false}
                    axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                    interval={tickInterval}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'rgba(255, 255, 255, 0.7)' }}
                    tickLine={false}
                    axisLine={{ stroke: 'rgba(255, 255, 255, 0.2)' }}
                    allowDecimals={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="count"
                    name="Agendadas"
                    stroke={CHART_COLORS.scheduled}
                    strokeWidth={2}
                    dot={{ fill: CHART_COLORS.scheduled, r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className={styles.noData}>
                <Caption1>Nenhum dado disponível</Caption1>
              </div>
            )}
          </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HistoryPage;
